/**
 * Manage Execution Templates Modal
 * Lists every Execution Template saved at the current scope (Process/ProcessThread/
 * ProcessElement) so the user can open any of them for editing, instead of the
 * "Create Template" button silently jumping to whichever template happened to be
 * first for that scope.
 */

import React from 'react';
import { useUIStore } from '@flow-studio/store'; // import { uiStore';
import { ExecutionPipelinesApiClient, ExecutionTemplate } from 'process-pipelines-common';
import { Modal } from '../Support/Modal';
import { Button } from '../Shared/FormUI';
import type { ScopeContext } from '../../hooks/useCreateTemplate';

export interface ManageExecutionTemplatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  apiClient: ExecutionPipelinesApiClient;
  scope: ScopeContext | null;
  onEditTemplate: (template: ExecutionTemplate) => void;
  onCreateNew: () => void;
}

export const ManageExecutionTemplatesModal: React.FC<ManageExecutionTemplatesModalProps> = ({
  isOpen,
  onClose,
  apiClient,
  scope,
  onEditTemplate,
  onCreateNew,
}) => {
  const { theme } = useUIStore();
  const [templates, setTemplates] = React.useState<ExecutionTemplate[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!isOpen || !scope) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    apiClient
      .getExecutionTemplatesByScope(scope.executionScope, scope.scopeReferenceID)
      .then((result) => {
        if (cancelled) return;
        setTemplates(result);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load execution templates');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, scope, apiClient]);

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '12px 14px',
    borderRadius: '8px',
    border: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e5e7eb',
    background: theme === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f9fafb',
  };

  const nameStyle: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: 600,
    color: theme === 'dark' ? '#f8fafc' : '#111827',
  };

  const metaStyle: React.CSSProperties = {
    fontSize: '12px',
    color: theme === 'dark' ? '#94a3b8' : '#6b7280',
    marginTop: '2px',
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={scope ? `Execution Templates — ${scope.scopeLabel}` : 'Execution Templates'}
      maxWidth="640px"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Close</Button>
          <Button variant="primary" onClick={onCreateNew}>Create New Template</Button>
        </>
      }
    >
      {loading && (
        <div style={{ padding: '24px 0', textAlign: 'center', color: theme === 'dark' ? '#94a3b8' : '#6b7280' }}>
          Loading execution templates…
        </div>
      )}

      {!loading && error && (
        <div style={{ padding: '16px', color: '#ef4444', fontSize: '14px' }}>
          Failed to load execution templates: {error}
        </div>
      )}

      {!loading && !error && templates.length === 0 && (
        <div style={{ padding: '24px 0', textAlign: 'center', color: theme === 'dark' ? '#94a3b8' : '#6b7280' }}>
          No execution templates exist yet for this scope.
        </div>
      )}

      {!loading && !error && templates.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {templates.map((template) => (
            <div key={template.executionTemplateID} style={rowStyle}>
              <div style={{ minWidth: 0 }}>
                <div style={nameStyle}>{template.name || '(untitled)'}</div>
                <div style={metaStyle}>
                  {template.slug}
                  {template.description ? ` — ${template.description}` : ''}
                  {!template.enabled ? ' • Disabled' : ''}
                </div>
              </div>
              <Button variant="secondary" onClick={() => onEditTemplate(template)}>
                Open / Edit
              </Button>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};
